import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as projectInitialState } from '~redux/slices/project';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import * as Styles from './_projectManagementSearchAndFilterBar.module.scss';
import { TGetProjectListingFilters } from '~types/feat/project';
import { MetalTypeEnum, ProjectAccStatusEnum } from '~utils/enums';

const ProjectManagementSearchAndFilterBar = () => {
    const filters = useAppSelector(state => state.project.projectListing.filters);
    const initialFilters = projectInitialState.projectListing.filters;

    const dispatch = useAppDispatch();

    const setFilters = (inputFilters: TGetProjectListingFilters) => dispatch(combinedActions.projectSetGetProjectListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.projectGetProjectListingAttempt());

    useEffect(() => {
        setFilters({
            ...filters,
        });
    }, []);

    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
        });
        dispatch(combinedActions.projectGetProjectListingAttempt());
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Project Name:',
            type: 'input' as const,
            inputParams: {
                value: filters.projectName,
                setValue: (event) => setFilters({ ...filters, projectName: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Status:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Active',
                        value: ProjectAccStatusEnum.Active,
                    },
                    {
                        label: 'Deactivated',
                        value: ProjectAccStatusEnum.Deactivated,
                    },
                ],
                value: filters.status,
                setValue: (event) => setFilters({ ...filters, status: event.target.value }),
                optionSelected: !!ProjectAccStatusEnum[filters.status as keyof typeof ProjectAccStatusEnum],
                noOptionsMessage: 'No status found',
                className: Styles.input,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Gold',
                        value: MetalTypeEnum.Gold,
                    },
                    {
                        label: 'Silver',
                        value: MetalTypeEnum.Silver,
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, metalType: event.target.value }),
                optionSelected: !!MetalTypeEnum[filters.metalType as keyof typeof MetalTypeEnum],
                noOptionsMessage: 'No metal type found',
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom,
                setDate: (date: string) => {
                    if (date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                maxDate: dayjs().toISOString(),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo,
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default ProjectManagementSearchAndFilterBar;
